import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { GA_TRACKING_ID, analyticsEnabled, trackEvent } from '../utils/analytics';

// Load gtag.js (~180 KB, its parse/execute tasks are the longest on the main
// thread) on the visitor's first interaction, or 7 s after the page has loaded
// if they never interact. Keeps it out of the load/TBT window; visitors who
// leave within ~7 s without touching the page are not counted.
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
const FALLBACK_DELAY_MS = 7000;

const whenIdle = (callback) => {
  let fired = false;
  let timer;
  const fire = () => {
    if (fired) return;
    fired = true;
    clearTimeout(timer);
    INTERACTION_EVENTS.forEach((name) => window.removeEventListener(name, fire));
    callback();
  };
  INTERACTION_EVENTS.forEach((name) => window.addEventListener(name, fire, { once: true, passive: true }));
  const arm = () => { timer = setTimeout(fire, FALLBACK_DELAY_MS); };
  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm, { once: true });
};

export default function AnalyticsTracker() {
  const location = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!analyticsEnabled()) return undefined;
    whenIdle(() => {
      ReactGA.initialize(GA_TRACKING_ID);
      setReady(true);
    });

    // Phone and email clicks anywhere on the site count as contact conversions.
    const onClick = (event) => {
      const link = event.target.closest && event.target.closest('a[href^="tel:"], a[href^="mailto:"]');
      if (!link || !ReactGA.isInitialized) return;
      const href = link.getAttribute('href');
      const isPhone = href.startsWith('tel:');
      trackEvent(isPhone ? 'click_phone' : 'click_email', {
        link_url: href,
        page_path: window.location.pathname,
      });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    if (ready) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
  }, [location, ready]);

  return null;
}
